import React from "react";
import { ConfigProvider, Slider, Switch, SwitchProps, Tag } from "antd";
import styled from "styled-components";
import { CompanyRangeSlider } from "./CompanyRangeSlider";
import {
  FlexibleContainer,
  HorizontalLine,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { compassLightGreen, lightGreyText } from "../../../styles";

const SliderContainer = styled.div`
  width: 250px;
`;

export const ScatterControls = ({
  isLoading,
  maxClusters,
  setMaxClusters,
  maxClusterSlider,
  includeNonEngaged,
  setIncludeNonEngaged,
  totalCompanies,
  onRangeChange,
}: ScatterControls): JSX.Element => {
  return (
    <FlexibleContainer fd="column" ai="flex-start" f={1}>
      <RowContainer>
        <CompassParagraph fontSize={14}>Configuration</CompassParagraph>
      </RowContainer>
      <HorizontalLine marginBottom={4} marginTop={1} />
      <RowContainer mb={1}>
        <CompassParagraph color={lightGreyText}>
          Clustering Intensity
        </CompassParagraph>
        <Tag bordered={false}>{maxClusters}</Tag>
      </RowContainer>
      <RowContainer mt={1}>
        <SliderContainer>
          <Slider
            disabled={isLoading || maxClusterSlider <= 4}
            min={4}
            max={maxClusterSlider}
            value={maxClusters}
            onChange={setMaxClusters}
            tooltip={{ formatter: (v): string => `Max ${v} clusters` }}
          />
        </SliderContainer>
      </RowContainer>
      <RowContainer mb={1}>
        <CompassParagraph color={lightGreyText}>Company Range</CompassParagraph>
        <Tag bordered={false}>{totalCompanies}</Tag>
      </RowContainer>
      <RowContainer mt={1}>
        <SliderContainer>
          <CompanyRangeSlider
            isLoading={isLoading}
            totalCompanies={totalCompanies}
            onRangeChange={onRangeChange}
          />
        </SliderContainer>
      </RowContainer>

      <RowContainer>
        <CompassParagraph color={lightGreyText}>
          Include Unengaged Companies
        </CompassParagraph>
      </RowContainer>
      <RowContainer mt={1}>
        <CustomSwitch
          checked={includeNonEngaged}
          onChange={setIncludeNonEngaged}
        />
      </RowContainer>
    </FlexibleContainer>
  );
};

export const CustomSwitch = (props: SwitchProps): JSX.Element => (
  <ConfigProvider
    theme={{
      components: {
        Switch: {
          handleBg: props.checked ? compassLightGreen : "white",
          trackMinWidth: 34,
          colorPrimary: "#CFFFEB",
          colorPrimaryHover: "#CFFFEB",
          handleShadow: "none",
        },
      },
    }}
  >
    <Switch {...props} />
  </ConfigProvider>
);

type ScatterControls = {
  isLoading: boolean;
  maxClusters: number;
  setMaxClusters: (n: number) => void;
  maxClusterSlider: number;
  includeNonEngaged: boolean;
  setIncludeNonEngaged: (b: boolean) => void;
  totalCompanies: number;
  onRangeChange: (range: [number, number]) => void;
  clustersCount: number;
};
